import { Box, ExtendedLinkProps, Flex } from "@xcorejs/ui";
import ApplicationPicker from "components/ApplicationPicker";
import Navigation, { NavigationProps } from "design-system/appio/navigation";
import { memo, useCallback, useState } from "react";
import useDelayedAction from "hooks/useDelayedAction";
import { useLayout } from "xcore";
import { useRouter } from "next/router";
import { useUser } from "hooks/useUser";
import SubheaderItem, { DefaultSubheaderActiveNavLinkStyle } from "./SubheaderItem";
import useSubHeaderStore from "./useSubHeaderStore";
import { ProductOverviewPage, ProductOverviewPageValues } from "xcore/types";
import SubheaderItemDropdown from "./SubheaderItemDropdown";

const Subheader = ({ leftContent, middleContent, rightContent, ...props }: NavigationProps) => {
  const { user } = useUser();
  const { isPreview } = useRouter();
  const isLogged = !!user;
  const { showSubheader } = useSubHeaderStore();
  if (!showSubheader) {
    return null;
  }

  const isLoggedInUserAndIsPreview = isLogged && isPreview;

  return (
    <Navigation
      leftContent={leftContent}
      middleContent={middleContent}
      rightContent={rightContent}
      height={{ _: "3.6rem", md: "4.6rem" }}
      bg="blackRGB.o9"
      _container={{ justifyContent: "space-between", alignItems: "center" }}
      _grid={{ columns: "auto 1fr auto", rows: "auto" }}
      position="sticky"
      zIndex={1002}
      display={{ _: "none", md: "flex" }}
      // compute offset from top based on whether user is logged in and whether preview mode is activated (otherwise space for logged in strip and preview strip must be left)
      {...{
        top: isLoggedInUserAndIsPreview ? "14.8rem" : !isLogged && !isPreview ? "8.4rem" : "11.6rem",
      }}
      {...props}
    />
  );
};

export default Subheader;

export const DefaultLeftContent = () => (
  <Flex alignItems="center">
    <ApplicationPicker />
  </Flex>
);

export const DefaultMiddleContent = memo(() => {
  const {
    subheader: { items: sortedItems },
    stringify,
    localize,
  } = useLayout();
  const c = useCallback(() => setActiveItem(undefined), []);
  const [close, prevent] = useDelayedAction(c, 300);

  const [activeItem, setActiveItem] = useState<number>();
  const sortedItemsFiltered = sortedItems.filter((item) => item && item?.id && item?.values);

  return (
    <Box onMouseLeave={() => close()} onMouseEnter={prevent}>
      <Flex alignItems="center" justifyContent="center" role="group">
        {/* Nav items */}
        {sortedItemsFiltered?.map((r, i) => {
          return (
            <SubheaderItem
              key={i}
              isActiveItem={!!activeItem}
              set={() => setActiveItem(r.id)}
              content={r}
              {...(activeItem && r.id === activeItem && { ...DefaultSubheaderActiveNavLinkStyle })}
            >
              {"thumbnailTitle" in r.values && stringify(r.values.thumbnailTitle) ? stringify(r.values.thumbnailTitle) : stringify(r.title)}
            </SubheaderItem>
          );
        })}
      </Flex>

      {/* Dropdown sections, initially hidden */}
      {sortedItemsFiltered?.map((p, i) => {
        const pop =
          activeItem === p.id
            ? ((sortedItems.find((item) => item?.id === activeItem && item?.type === "productOverviewPage") as ProductOverviewPage) ??
              undefined)
            : undefined;

        return (
          <SubheaderItemDropdown
            key={i}
            active={activeItem === p.id}
            pop={pop}
            empty={!localize((p?.values as ProductOverviewPageValues)?.itemsRelation)?.length}
          />
        );
      })}
    </Box>
  );
});

export const DefaultRightContent = () => <></>;

export const SubheaderNavLinkStyle: ExtendedLinkProps = {
  display: "flex",
  alignItems: "center",
  fontSize: "1.3rem",
  padding: { _: "0 .5rem", lg: "0 1rem" },
  height: "4.6rem",
  fontWeight: 600,
  lineHeight: "2.1rem",
  color: "lightGray",
  transition: "color 300ms, background 300ms",
  whiteSpace: "nowrap",
  textAlign: "center",
  _hover: {
    color: "#fff",
  },
};
